import {
  archiveContainerSourceKey,
  caseIdFromTags,
  encodeEvidenceLockerStyleProxyUriSegment,
  getFileInfoBodyFromServer,
  isArchiveMemberSearchablePath,
  isLikelyBase64MediaValue,
  s3UriToObjectKey,
} from "@/shared/functions";
import { fetchJunkdrawerUploadPresign } from "@/shared/cors-junkdrawer-api";
import { startCaptureSave, type CaptureSaveLog } from "@/shared/captureSaveLog";
import { storageUrlForDisplay } from "@/shared/storageUrlForDisplay";
import { getCurrentFileInfo } from "@/shared/clientFunctions";
import { detachCaptureVideo, loadSameOriginVideoFrame } from "@/shared/loadSameOriginVideoFrame";
import type { MediaSource } from "@/shared/types";
import { SOURCE_TYPES } from "@/shared/types";

export type UserImageUploadPurpose =
  | "capture-favorite"
  | "csam-capture"
  | "event-media-capture"
  | "video-still-frame"
  | "pdf-page-thumbnail";

export type UploadUserImageParams = {
  /** Data URL or raw base64 payload. */
  imageData: string;
  mimeType: "image/webp" | "image/png";
  purpose: UserImageUploadPurpose;
  caseId?: string;
  tenantFriendlyName?: string;
  /** MD5 of the original source file. Resolved from sourceKey when omitted. */
  sourceMd5?: string;
  sourceKey?: string;
  cellId: string;
  accessToken: string;
  /** Join an in-progress attempt's log instead of opening a new one. */
  log?: CaptureSaveLog;
};

export type UploadUserImageResult = {
  s3Uri: string;
};

/** Must match junkdrawer ``USER_IMAGE_UPLOAD_MAX_BYTES`` (10 MB). */
export const MAX_USER_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024;
const MD5_HEX_RE = /^[0-9a-f]{32}$/i;

/** Canonicalize a plain or ``md5:<hex>`` digest returned by source metadata. */
export function normalizeOriginMd5(value: string | null | undefined): string | null {
  const normalized = (value ?? "").trim().replace(/^md5:/i, "");
  return MD5_HEX_RE.test(normalized) ? normalized.toLowerCase() : null;
}

/** Extract the source digest from a processed ``files/<md5>/...`` object path. */
export function originMd5FromSourceKey(sourceKey: string | null | undefined): string | null {
  if (!sourceKey?.trim()) return null;
  let decoded = sourceKey.trim();
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    // Keep the original value; malformed percent encoding will simply not match.
  }
  const match = decoded.match(/(?:^|\/)files\/([0-9a-f]{32})(?:\/|$)/i);
  return normalizeOriginMd5(match?.[1]);
}

/**
 * Playback / ephemeral URLs cannot be used as a junkdrawer ``sourceKey``.
 * Presigned HTTPS, ``/api/proxy/``, ``blob:``, and ``data:`` have no stable
 * object identity, so origin-MD5 lookup and CSAM tag inheritance would fail
 * even when ``originalUrl`` / ``s3Url`` still name the storage object.
 *
 * HTTPS processed keys that already contain ``files/<md5>/`` are kept — those
 * are real objects, not playback-only URLs.
 */
export function isEphemeralPlaybackUrl(url: string | null | undefined): boolean {
  const t = (url ?? "").trim();
  if (!t) return true;
  if (t.startsWith("data:") || t.startsWith("blob:")) return true;
  if (t.includes("/api/proxy/")) return true;
  if (/^https?:\/\//i.test(t) && !originMd5FromSourceKey(t)) return true;
  return false;
}

/**
 * Bind a capture to a real storage object and origin MD5.
 *
 * ``originalUrl`` / searchable paths for zip/mbox members are virtual
 * (``data/bulk/…/archive.zip/inner``) — they have no ``files/<md5>/`` segment
 * and ``/files/file-info`` returns null. Prefer the extract's processed
 * ``fileUrl`` (or any other real object URL) so origin MD5 can be parsed or
 * looked up from the parent archive. Never prefer playback URLs over a
 * stable storage path.
 */
export function originBindingFromMedia(
  media: Pick<
    MediaSource,
    | "sourceInfo"
    | "originalUrl"
    | "fileInfo"
    | "sourceUrl"
    | "mediaUrl"
    | "mediaSrc"
    | "s3Url"
  > | null | undefined,
): { sourceKey?: string; sourceMd5?: string } {
  const candidates = [
    media?.sourceInfo?.sourceObjectUrl,
    media?.fileInfo?.fileUrl,
    media?.s3Url,
    media?.sourceUrl,
    media?.originalUrl,
    media?.mediaUrl,
    media?.mediaSrc,
  ];

  const storageCandidates = candidates.filter(
    (c) => !!c?.trim() && !isEphemeralPlaybackUrl(c),
  );
  const withPathMd5 =
    storageCandidates.find((c) => originMd5FromSourceKey(c ?? "")) ??
    candidates.find((c) => originMd5FromSourceKey(c ?? ""));
  const realObject = storageCandidates.find(
    (c) => !isArchiveMemberSearchablePath(c),
  );
  const fallback = storageCandidates.find((c) => !!c?.trim());
  const sourceKey = (withPathMd5 || realObject || fallback)?.trim() || undefined;

  const sourceMd5 =
    normalizeOriginMd5(media?.sourceInfo?.sourceContentDigest) ||
    originMd5FromSourceKey(sourceKey) ||
    normalizeOriginMd5(media?.fileInfo?.md5) ||
    undefined;

  return { sourceKey, sourceMd5 };
}

const SOURCE_TYPE_SEGMENTS = new Set(SOURCE_TYPES.map((type) => type.toLowerCase()));

/** True for the All Cases selector (`*`) and empty values — not a real intake case. */
export function isAggregateCaseId(caseId: string | null | undefined): boolean {
  const trimmed = (caseId ?? "").trim();
  return !trimmed || trimmed === "*" || trimmed.toLowerCase() === "all cases";
}

function storageKeyFromPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return "";
  const fromS3 = s3UriToObjectKey(trimmed);
  if (fromS3) return fromS3;
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      return decodeURIComponent(new URL(trimmed).pathname.replace(/^\/+/, ""));
    }
  } catch {
    // Keep the raw path when URL parsing fails.
  }
  return trimmed.replace(/^\/+/, "");
}

/**
 * Case folder from a bulk storage path (`data/bulk/<caseId>/<sourceType>/…`).
 * Returns undefined when the first segment is a source type (no case folder).
 */
export function caseIdFromStoragePath(path: string | null | undefined): string | undefined {
  if (!path?.trim()) return undefined;
  let key = storageKeyFromPath(path);
  try {
    key = decodeURIComponent(key);
  } catch {
    // Keep the original value; malformed percent encoding will simply not match.
  }
  const lower = key.toLowerCase();
  let afterBulk: string | undefined;
  const dataBulk = lower.indexOf("data/bulk/");
  if (dataBulk >= 0) {
    afterBulk = key.slice(dataBulk + "data/bulk/".length);
  } else {
    const slashBulk = lower.indexOf("/bulk/");
    if (slashBulk >= 0) {
      afterBulk = key.slice(slashBulk + "/bulk/".length);
    } else if (lower.startsWith("bulk/")) {
      afterBulk = key.slice("bulk/".length);
    }
  }
  if (afterBulk === undefined) return undefined;
  const first = afterBulk.split("/").find((segment) => segment.trim() !== "");
  if (!first) return undefined;
  if (SOURCE_TYPE_SEGMENTS.has(first.toLowerCase())) return undefined;
  if (isAggregateCaseId(first)) return undefined;
  return first;
}

export type ResolveUserImageUploadCaseIdArgs = {
  selectedCaseId?: string | null;
  media?: Pick<
    MediaSource,
    | "fileInfo"
    | "sourceInfo"
    | "sourceFilePath"
    | "originalUrl"
    | "sourceUrl"
    | "mediaUrl"
    | "mediaSrc"
    | "s3Url"
  > | null;
  sourceKey?: string | null;
};

/**
 * Case id for junkdrawer user-image uploads. Never returns All Cases (`*`):
 * the presign endpoint is case-scoped, and a sourceKey from a real file cannot
 * belong to the aggregate selector (TR-3001).
 *
 * This value becomes a literal folder name under ``user_generated_files/``, so
 * the candidates are ordered by which of them carry the case's real
 * capitalisation. Storage paths and ``searchablePath`` name the intake folder
 * itself and are authoritative. The provenance ``case:`` tag is not: it is a
 * matching key, lowercased where it is minted (entity-service builds it from
 * ``case_name.strip().lower()``). Reading the tag first meant one case uploaded
 * into both ``Jake Test Case`` and ``jake test case`` depending on whether the
 * media happened to have ``fileInfo.tags`` loaded at the time.
 */
export function resolveUserImageUploadCaseId(
  args: ResolveUserImageUploadCaseIdArgs,
): string | undefined {
  const selected = args.selectedCaseId?.trim();
  const concreteSelected =
    selected && !isAggregateCaseId(selected) ? selected : undefined;

  const fromSourceInfo = args.media?.sourceInfo?.sourceCaseId?.trim();
  if (fromSourceInfo && !isAggregateCaseId(fromSourceInfo)) return fromSourceInfo;

  const searchable = args.media?.fileInfo?.searchablePath?.trim();
  if (searchable) {
    const fromSearchable = caseIdFromStoragePath(
      searchable.toLowerCase().includes("bulk/") ? searchable : `data/bulk/${searchable}`,
    );
    if (fromSearchable) return fromSearchable;
  }

  const pathCandidates = [
    args.sourceKey,
    args.media?.sourceFilePath,
    args.media?.originalUrl,
    args.media?.sourceUrl,
    args.media?.s3Url,
    args.media?.mediaUrl,
    args.media?.mediaSrc,
  ];
  for (const candidate of pathCandidates) {
    const fromPath = caseIdFromStoragePath(candidate);
    if (fromPath) return fromPath;
  }

  // A processed-only object (``files/<md5>/…``) has no case folder to read, so
  // the tag is the last signal identifying the case. Take the capitalisation
  // back from the selected case when both name the same one, so the fallback
  // still writes into the canonical folder.
  const fromTags = caseIdFromTags(args.media?.fileInfo?.tags);
  if (fromTags && !isAggregateCaseId(fromTags)) {
    return concreteSelected?.toLowerCase() === fromTags.toLowerCase()
      ? concreteSelected
      : fromTags;
  }

  return concreteSelected;
}

async function provenanceMd5ForLookup(
  lookupKey: string,
  cellId: string,
  accessToken: string,
): Promise<string | null> {
  const fileInfoBody = await getFileInfoBodyFromServer(
    lookupKey,
    cellId,
    accessToken,
  );
  if (fileInfoBody?.kind === "file") {
    return normalizeOriginMd5(fileInfoBody.file.md5);
  }
  if (fileInfoBody?.kind === "subdocument") {
    const rootSourceUrl = fileInfoBody.hierarchy?.[0]?.s3Url;
    if (rootSourceUrl) {
      const rootFileInfo = await getCurrentFileInfo(
        rootSourceUrl,
        cellId,
        accessToken,
      );
      return normalizeOriginMd5(rootFileInfo?.md5);
    }
    return normalizeOriginMd5(fileInfoBody.subdocument.md5);
  }
  return null;
}

/**
 * Where the origin MD5 came from. Logged because the two ``provenance`` routes
 * are the only ones that make a cell call, so they are the only ones that can
 * fail for a stale token or a network blip.
 */
type OriginMd5Route =
  | "sourceMd5"
  | "sourceKeyPath"
  | "provenanceContainer"
  | "provenance";

async function resolveOriginMd5(
  params: UploadUserImageParams,
): Promise<{ md5: string; route: OriginMd5Route }> {
  const explicit = normalizeOriginMd5(params.sourceMd5);
  if (explicit) return { md5: explicit, route: "sourceMd5" };

  const sourceKey = params.sourceKey?.trim();
  if (!sourceKey) {
    throw new Error("uploadUserImage requires sourceKey for origin MD5");
  }

  const pathMd5 = originMd5FromSourceKey(sourceKey);
  if (pathMd5) return { md5: pathMd5, route: "sourceKeyPath" };

  const containerKey = archiveContainerSourceKey(sourceKey);
  if (containerKey) {
    const containerMd5 = await provenanceMd5ForLookup(
      containerKey,
      params.cellId,
      params.accessToken,
    );
    if (containerMd5) return { md5: containerMd5, route: "provenanceContainer" };
  }

  const provenanceMd5 = await provenanceMd5ForLookup(
    sourceKey,
    params.cellId,
    params.accessToken,
  );
  if (!provenanceMd5) {
    // ``getFileInfoBodyFromServer`` returns null for a failed lookup as well as
    // for a file it has no MD5 for, so this one message covers both. A cell
    // failure normally leaves an ``[auth-recovery]`` warning just above it.
    throw new Error(
      "Unable to resolve the original file MD5 for user image upload " +
        "(provenance returned no MD5, or the file-info lookup failed)",
    );
  }
  return { md5: provenanceMd5, route: "provenance" };
}

export function stripWebpToRawBase64(input: string): string {
  const t = input.trim().replace(/\s/g, "");
  const m = /^data:image\/(?:webp|png);base64,(.+)$/i.exec(t);
  return m ? m[1]! : t;
}

/**
 * Capture a single video frame as a WebP data URL.
 *
 * Uses the same-origin ``/api/proxy`` path so the canvas is not tainted by
 * cross-origin presigned S3 playback URLs (``toDataURL`` would throw otherwise).
 */
export async function captureVideoFrameDataUrl(
  sourceObjectUrl: string,
  timeSeconds: number,
  canvasWidth = 120,
): Promise<string> {
  const proxySrc = `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(sourceObjectUrl)}`;
  const video = await loadSameOriginVideoFrame(proxySrc, timeSeconds);
  try {
    const aspectRatio =
      video.videoWidth > 0 && video.videoHeight > 0
        ? video.videoWidth / video.videoHeight
        : 1;
    const canvasHeight = canvasWidth / aspectRatio;
    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas not available');
    }
    ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight);
    return canvas.toDataURL('image/webp');
  } finally {
    detachCaptureVideo(video);
  }
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

export type CaptureEntryLike = {
  s3Uri?: string;
  webpBase64?: string;
};

export type StagedCaptureData = {
  s3Uri?: string;
  sourceKey?: string;
  caseId?: string;
  /**
   * Identity of the *citation* the capture was taken from (file plus cited
   * page). ``sourceKey`` only names the file, and one answer can cite several
   * passages of the same document, so the file alone cannot keep a crop from
   * surfacing on a passage it does not depict (TR-3040).
   */
  citationKey?: string;
  /** @deprecated legacy staging — data URL from before S3 migration */
  captureData?: string;
  /** @deprecated use sourceKey */
  s3Key?: string;
};

export function parseStagedCaptureFromLocalStorage(
  raw: string | null,
): StagedCaptureData | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as StagedCaptureData;
  } catch {
    return undefined;
  }
}

export function stagedCaptureImageRef(obj: StagedCaptureData | undefined): string {
  if (!obj) return "";
  if (obj.s3Uri?.trim()) return obj.s3Uri.trim();
  return obj.captureData?.trim() || "";
}

export function hasStagedCapture(obj: StagedCaptureData | undefined): boolean {
  return stagedCaptureImageRef(obj) !== "";
}

function normalizeStorageKeyForCompare(key: string): string {
  let value = key.trim();
  try {
    value = decodeURIComponent(value);
  } catch {
    // Keep the original value; malformed percent encoding will simply not match.
  }
  const fromS3 = s3UriToObjectKey(value);
  if (fromS3) value = fromS3;
  const query = value.indexOf("?");
  if (query >= 0) value = value.slice(0, query);
  return value.replace(/^\/+/, "").toLowerCase();
}

/** True when two junkdrawer/S3 pointers name the same origin object. */
export function sourceKeysReferToSameOrigin(
  left: string | null | undefined,
  right: string | null | undefined,
  extraMd5?: string | null,
): boolean {
  const a = left?.trim();
  const b = right?.trim();
  if (!a || !b) return false;
  if (normalizeStorageKeyForCompare(a) === normalizeStorageKeyForCompare(b)) return true;
  const md5A = originMd5FromSourceKey(a);
  const md5B = originMd5FromSourceKey(b) || normalizeOriginMd5(extraMd5);
  return Boolean(md5A && md5B && md5A === md5B);
}

function mediaSourceKeyCandidates(
  media: Parameters<typeof originBindingFromMedia>[0],
): string[] {
  const binding = originBindingFromMedia(media);
  return [
    binding.sourceKey,
    media?.s3Url,
    media?.originalUrl,
    media?.sourceUrl,
    media?.mediaUrl,
    media?.mediaSrc,
  ].filter((value): value is string => Boolean(value?.trim()));
}

/**
 * A staged ``captureData`` localStorage entry may only paint or apply on the
 * file (and case) it was taken from. The write already stores ``sourceKey`` /
 * ``caseId``; the read previously ignored them, so one capture leaked onto
 * every source and across cases (TR-2963).
 *
 * File and case are not enough on a Sources list where one answer cites
 * several passages of the same document: every citation shares the file's md5.
 * When the caller knows which citation it is painting it passes
 * ``citationKey``, and the entry must then name that same citation (TR-3040).
 * Callers that have no citation to speak of (single-file viewers) omit it and
 * keep the file-and-case gate.
 */
export function stagedCaptureBelongsToMedia(
  staged: StagedCaptureData | undefined,
  media: Parameters<typeof originBindingFromMedia>[0],
  selectedCaseId?: string | null,
  citationKey?: string | null,
): boolean {
  if (!hasStagedCapture(staged)) return false;
  const storedKey = (staged?.sourceKey || staged?.s3Key || "").trim();
  if (!storedKey) return false;

  const wantedCitation = citationKey?.trim();
  if (wantedCitation) {
    // An entry with no citation of its own predates this scoping (or came from
    // a viewer that has no citations). Under per-citation isolation it cannot
    // be shown to depict this passage; the next capture re-stages it.
    if (staged?.citationKey?.trim() !== wantedCitation) return false;
  }

  const binding = originBindingFromMedia(media);
  const keyMatches = mediaSourceKeyCandidates(media).some((candidate) =>
    sourceKeysReferToSameOrigin(storedKey, candidate, binding.sourceMd5),
  );
  if (!keyMatches) return false;

  const storedCase = staged?.caseId?.trim();
  if (!storedCase || isAggregateCaseId(storedCase)) return true;

  const mediaCase = resolveUserImageUploadCaseId({
    selectedCaseId,
    media,
    sourceKey: binding.sourceKey || storedKey,
  });
  if (!mediaCase || isAggregateCaseId(mediaCase)) return true;
  return mediaCase.trim().toLowerCase() === storedCase.toLowerCase();
}

/**
 * Upload a user-generated image via junkdrawer presigned PUT into the tenant
 * processed bucket at
 * ``user_generated_files/<caseId>/<userId>/<originMd5>/``:
 *
 * 1. POST /junkdrawer-service/v1/pre-sign/upload → { presignedUrl, s3Uri, objectTagging }
 * 2. PUT image bytes to presignedUrl (Content-Length must match contentLength;
 *    x-amz-tagging must match objectTagging)
 *
 * For ``csam-capture``, pass ``sourceKey`` of the parent CSAM image so its S3
 * object tags are copied onto the capture.
 */
export async function uploadUserImage(
  params: UploadUserImageParams,
): Promise<UploadUserImageResult> {
  const log =
    params.log ??
    startCaptureSave({ purpose: params.purpose, caseId: params.caseId ?? null });

  const rawBase64 = stripWebpToRawBase64(params.imageData);
  const caseId = (params.caseId ?? "").trim();
  if (!caseId || isAggregateCaseId(caseId)) {
    const error = new Error("uploadUserImage requires a concrete caseId (not All Cases)");
    log.failed("resolve-case", error, { caseId: params.caseId ?? null });
    throw error;
  }
  if (!params.cellId || !params.accessToken) {
    const error = new Error("uploadUserImage requires cellId and accessToken");
    log.failed("resolve-case", error, {
      hasCellId: Boolean(params.cellId),
      hasAccessToken: Boolean(params.accessToken),
    });
    throw error;
  }
  const sourceKey = params.sourceKey?.trim();
  if (!sourceKey) {
    const error = new Error("uploadUserImage requires sourceKey");
    log.failed("resolve-case", error, { purpose: params.purpose });
    throw error;
  }

  const blob = base64ToBlob(rawBase64, params.mimeType);
  if (blob.size > MAX_USER_IMAGE_UPLOAD_BYTES) {
    const error = new Error(
      `Image exceeds maximum upload size of ${MAX_USER_IMAGE_UPLOAD_BYTES} bytes`,
    );
    log.failed("resolve-case", error, {
      bytes: blob.size,
      maxBytes: MAX_USER_IMAGE_UPLOAD_BYTES,
    });
    throw error;
  }
  // The case id becomes a literal S3 folder, so log the resolved value: two
  // spellings of one case mean uploads are landing in two places.
  log.step("resolve-case", { caseId, bytes: blob.size, sourceKey });

  let originMd5: string;
  try {
    const resolved = await resolveOriginMd5(params);
    originMd5 = resolved.md5;
    log.step("origin-md5", { originMd5, route: resolved.route });
  } catch (error) {
    log.failed("origin-md5", error, { sourceKey });
    throw error;
  }

  let presignedUrl: string;
  let s3Uri: string;
  let objectTagging: string;
  try {
    ({ presignedUrl, s3Uri, objectTagging } = await fetchJunkdrawerUploadPresign({
      cellId: params.cellId,
      accessToken: params.accessToken,
      body: {
        mimeType: params.mimeType,
        purpose: params.purpose,
        caseId,
        originMd5,
        contentLength: blob.size,
        tenantFriendlyName: params.tenantFriendlyName,
        sourceKey,
      },
    }));
  } catch (error) {
    log.failed("presign", error, { caseId, originMd5, bytes: blob.size });
    throw error;
  }
  log.step("presign", { s3Uri });

  let putResponse: Response;
  try {
    putResponse = await fetch(presignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": params.mimeType,
        "x-amz-tagging": objectTagging,
      },
      body: blob,
    });
  } catch (error) {
    log.failed("s3-put", error, { s3Uri, bytes: blob.size });
    throw error;
  }
  if (!putResponse.ok) {
    const detail = await putResponse.text().catch(() => "");
    const error = Object.assign(
      new Error(`S3 upload failed (${putResponse.status})${detail ? `: ${detail}` : ""}`),
      { status: putResponse.status },
    );
    log.failed("s3-put", error, { s3Uri });
    throw error;
  }
  log.step("s3-put", { s3Uri, bytes: blob.size });

  return { s3Uri };
}

/** Alias for user-generated image refs (S3 URI, path, data URL, or legacy base64). */
export function resolveUserImageUrl(ref: string | undefined | null): string {
  return storageUrlForDisplay(ref);
}

/** True when the value is an embedded data URL or raw base64 blob. */
export function isEmbeddedImageRef(value: string | undefined | null): boolean {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("data:")) return true;
  return isLikelyBase64MediaValue(trimmed);
}

/** Display URL for a favorites capture entry (legacy base64 or S3 URI). */
export function captureEntryDisplayUrl(entry: CaptureEntryLike): string {
  if (entry.webpBase64 && entry.webpBase64.length > 0) {
    return `data:image/webp;base64,${entry.webpBase64}`;
  }
  return resolveUserImageUrl(entry.s3Uri);
}

/** Image ref to persist on event media (S3 URI preferred; legacy data URL passthrough). */
export function captureEntryImageRef(entry: CaptureEntryLike): string {
  if (entry.s3Uri && entry.s3Uri.trim()) {
    return entry.s3Uri.trim();
  }
  if (entry.webpBase64 && entry.webpBase64.length > 0) {
    return `data:image/webp;base64,${entry.webpBase64}`;
  }
  return "";
}
